import React from 'react';
import { Label } from '@/components/ui/label';
import { FormFieldRenderer } from '@/components/forms/FormFieldRenderer';
import { detectFieldType, buildTypesFromTypeString } from '@/lib/formTypes';
import { setFormValue } from '@/lib/formApi';
import { isAuditField } from '@/lib/auditFields';

// ============================================================================
// LayoutField — рендер ОДНОГО поля макета кастомной формы (вынесено из
// CustomLayoutRenderer case 'field', ~100 строк — снижение цикломатики).
// Единый рендер через FormFieldRenderer (дополнения/подсказки/форматы/виды);
// значение читается из formStore (formKey+attrKey), изменения — setFormValue.
// ============================================================================

interface LayoutFieldProps {
  el: any;
  idx: number;
  formKey: string;
  values: Record<string, unknown>;
  displayNames: Record<string, string>;
  setDisplayNames: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  attributes: any[];
  hiddenFields: Record<string, boolean>;
  disabled?: boolean;
  readOnly?: boolean;
  onPick?: (attr: any) => void;
  renderByViewMode: (el: any, value: unknown, onChange: (v: unknown) => void, disabled: boolean, readOnly: boolean) => React.ReactNode;
}

export function LayoutField({ el, idx, formKey, values, displayNames, setDisplayNames, attributes, hiddenFields, disabled, readOnly, onPick, renderByViewMode }: LayoutFieldProps) {
  // Пишет в formStore + шину form.value_changed; очистка удаляет displayName
  const commit = (attr: string, v: unknown) => {
    setFormValue(formKey, attr, v);
    if (!v) {
      setDisplayNames((prev) => {
        const n = { ...prev };
        delete n[attr];
        return n;
      });
    }
  };

  if (hiddenFields[el.attribute]) return null;
  const attrKey = (el.attribute || '').split('.').pop() || el.attribute || '';
  // СИСТЕМНЫЕ аудит-поля (Создал/Дата создания/Изменил/Дата изменения) в макете
  // НЕ рендерятся: единая точка показа — AuditStatusBar в самом низу формы
  // (ObjectFormBody); в потоке макета их нет (ни полей ввода, ни рамок группы).
  if (isAuditField(attrKey) || isAuditField(el.attribute || '')) return null;
  const valueKey = (el.attribute || '').split('.').pop() || el.attribute || '';
  const isFillWidth = el.fillWidth || el.fillWidth === '';
  const attr = attributes.find((a: any) => a.name === el.attribute) || attributes.find((a: any) => a.name === attrKey);
  const fieldDisabled = disabled;
  // ГОЛОСОВОЙ ВВОД: у ЗАЯВОК (все базы, включая будущие) микрофон доступен во ВСЕХ
  // текстовых полях (платформенное правило); у остальных типов — по пометке
  // реквизита voice_input (раньше в кастомные формы флаг вообще не доходил).
  const voiceInputFor = (dt: string): boolean => {
    const a = attr as any;
    if (a && (a.voiceInput === true || a.voice_input === true)) return true;
    const t = String(dt || '').toLowerCase();
    return (formKey || '').split('.')[0] === 'order' && (t === 'text' || t === 'string');
  };

  // Fallback: поле не в metadata/attributes (макет хранит тип в элементе)
  if (!attr) {
    const fallbackType = el.fieldType || detectFieldType(el.type || '');
    const isRef = fallbackType === 'reference' || fallbackType === 'select';
    const fallbackTypes = isRef ? buildTypesFromTypeString(el.type || '') : [];
    if (isRef) {
      const colAttr = { name: el.attribute, type: el.type, types: fallbackTypes, comment: '' };
      return (
        <div key={`f-${el.attribute}-${idx}`} className={`space-y-1 ${isFillWidth ? 'w-full' : ''}`}>
          {el.label && el.showLabel !== false && <Label className="text-xs">{el.label}</Label>}
          <FormFieldRenderer
            field={{ name: valueKey, label: '', data_type: 'reference', ref_table: (el as any).refTable || '', placeholder: '', extensions: (el as any).extensions, is_read_only: false }}
            value={String(values[valueKey] ?? '')}
            displayValue={displayNames[valueKey] || String(values[valueKey + '_presentation'] ?? '')}
            onChange={(v) => commit(valueKey, v)}
            onPick={fallbackTypes.length > 0 && !fieldDisabled ? () => onPick?.(colAttr) : undefined}
            disabled={fieldDisabled}
            formValues={values}
            compact
          />
        </div>
      );
    }
    return (
      <div key={`f-${el.attribute}-${idx}`} className={`space-y-1 ${isFillWidth ? 'w-full' : ''}`}>
        {el.label && el.showLabel !== false && <Label className="text-xs text-muted-foreground">{el.label}</Label>}
        {(el.viewMode || 'input') === 'input' ? (
          <FormFieldRenderer
            field={{ name: valueKey, label: '', data_type: fallbackType, ref_table: '', placeholder: '', extensions: (el as any).extensions, voiceInput: voiceInputFor(String(fallbackType)), is_read_only: false }}
            value={String(values[valueKey] ?? '')}
            displayValue={displayNames[valueKey] || String(values[valueKey + '_presentation'] ?? '')}
            onChange={(v) => commit(valueKey, v)}
            disabled={fieldDisabled}
            formValues={values}
            compact
          />
        ) : (
          renderByViewMode(el, values[valueKey], (v) => commit(valueKey, v), fieldDisabled ?? false, readOnly ?? false)
        )}
      </div>
    );
  }

  const types = attr.types || [];
  const isAnyRef = String(attr.type || '').toLowerCase() === 'anyref';
  const fieldType = detectFieldType(attr.type || '', (attr as any).fkTable);
  // Перечисление НЕ ссылка: у него в fkTable — код перечисления; пикер не нужен
  // (FormFieldRenderer отрендерит селект значений по options).
  const hasPicker = fieldType !== 'enum' && (isAnyRef || types.length > 0 || !!(attr as any).fkTable);
  const fkTable = (attr as any).fkTable;
  let fieldValue = values[attrKey] as string ?? '';
  if (fieldType === 'date' && typeof fieldValue === 'string' && fieldValue.includes('T')) fieldValue = fieldValue.split('T')[0];

  if (el.viewMode === 'label' && fieldType !== 'file') {
    return (
      <div key={`f-${el.attribute}-${idx}`} className={isFillWidth ? 'w-full' : undefined}>
        {el.label && el.showLabel !== false && <Label className="text-xs text-muted-foreground">{el.label}</Label>}
        <div className="text-sm min-h-8 flex items-center">{fieldValue || '—'}</div>
      </div>
    );
  }
  return (
    <div key={`f-${el.attribute}-${idx}`} className={isFillWidth ? 'w-full' : undefined}>
      <FormFieldRenderer
        field={{
          name: attrKey,
          label: el.label || '',
          data_type: isAnyRef ? 'anyref' : (fieldType === 'reference' ? 'reference' : (attr.type || 'text')),
          ref_table: fkTable || '',
          placeholder: '',
          extensions: (el as any).extensions,
          is_read_only: readOnly || false,
          viewMode: el.viewMode as string | undefined,
          action: (el as any).action as string | undefined,
          cardFields: (el as any).cardFields as string[] | undefined,
          // Перечисление: значения для селекта (загружены моделью формы)
          options: (attr as any).options,
          // ГОЛОСОВОЙ ВВОД: РАНЬШЕ главная ветка (реальные поля) voiceInput НЕ
          // передавала — микрофона на кастомных формах (в т.ч. заявок cityecon)
          // не было вовсе (работала только fallback-ветка и автоформы).
          voiceInput: voiceInputFor(String(attr.type || 'text')),
          // Объект-родитель: правила default_for дополнений (object_type/object_code)
          parentType: (attr as any).parentType,
          parentCode: (attr as any).parentCode,
        }}
        value={fieldValue}
        displayValue={displayNames[attrKey] || String(values[attrKey + '_presentation'] ?? '')}
        className={isFillWidth ? 'w-full' : ''}
        onChange={(v) => commit(attrKey, v)}
        onPick={hasPicker && !fieldDisabled ? () => onPick?.(attr) : undefined}
        disabled={fieldDisabled}
        formValues={values}
        labelPosition={el.showLabel === false ? 'none' : ((el as any).labelPosition || 'top')}
      />
    </div>
  );
}
